import { useEffect } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { FormProvider, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useLens } from "@hookform/lenses"
import { Button } from "@/components/ui/button"
import { CardBodyContent, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { FormStatus } from "@/components/ui/form"
import { BetterDocRejectionReasonDialog } from "@/modules/patients/detail/better-doc/components/better-doc-rejection-reason-dialog"
import { DeBetterDocResult } from "@/modules/patients/detail/better-doc/de/components/de-better-doc-result"
import { useBetterDocRejectionConfirmation } from "@/modules/patients/detail/better-doc/hooks/useBetterDocRejectionConfirmation"
import { PointOfCareContent } from "@/modules/patients/detail/progress-note/point-of-care/components/point-of-care-content"
import { ProgressNoteSelect } from "@/modules/patients/detail/progress-note/point-of-care/components/progress-note-select"
import {
  ACTUAL_POINT_OF_CARE_FIELD,
  MEDICAL_POINT_OF_CARE_FIELD,
  POINT_OF_CARE_NOT_SELECTABLE_AS_ACTUAL,
} from "@/modules/patients/detail/progress-note/point-of-care/constants"
import { useAutoSubmitPointOfCareForm } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useAutoSubmitPointOfCareForm"
import { useInitializePointOfCare } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useInitializePointOfCare"
import { useSelectPointOfCare } from "@/modules/patients/detail/progress-note/point-of-care/hooks/useSelectPointOfCare"
import { usePointOfCareContext } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import type { PointOfCareOption } from "@/modules/patients/detail/progress-note/point-of-care/types"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useReferralStoreContext } from "@/modules/patients/detail/referral/store/useReferralStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { MedgateServiceInfo } from "@/modules/patients/services/usePatientByIdQuery"
import { PointOfCareType } from "@/types/__generated__/union-types"

type PointOfCareSectionProps = {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  patientId: string
  medgateServiceInfo: MedgateServiceInfo
  diagnosisCode: string
}

const isSelectableAsActual = (medicalPointOfCare: PointOfCareType | undefined) => {
  return (option: PointOfCareOption): boolean =>
    option.value !== medicalPointOfCare && !POINT_OF_CARE_NOT_SELECTABLE_AS_ACTUAL.includes(option.value)
}

export const PointOfCareSection = (props: PointOfCareSectionProps): React.JSX.Element => {
  const country = useCountryParam()
  const { episodeOfCareProgressNoteData, medgateServiceInfo, diagnosisCode, patientId } = props

  const { t } = useTranslation()
  const { form, hasCareProvider } = useInitializePointOfCare(country, episodeOfCareProgressNoteData)
  const { pointOfCareItems, timeToTreatByPointOfCare } = usePointOfCareContext()

  const { onMedicalPointOfCareSelect, onActualPointOfCareSelect, onSpecialtySelect, setCareProvider } =
    useSelectPointOfCare({
      form,
      episodeOfCareProgressNoteData,
      patientId,
      countryCode: country,
      medgateServiceInfo,
      diagnosisCode,
    })

  useAutoSubmitPointOfCareForm({ form, episodeOfCareProgressNoteData, countryCode: country })

  const {
    onBetterDocCheck,
    isRejectionDialogOpen,
    isConfirmingBetterDocRejection,
    handleRejectionCancel,
    handleRejectionConfirm,
    handleRejectionDialogCloseAutoFocus,
  } = useBetterDocRejectionConfirmation({
    form,
    countryCode: country,
    progressNoteId: episodeOfCareProgressNoteData.id,
    confirmedBetterDocRejectionReason: episodeOfCareProgressNoteData.betterDoc?.rejectionReason,
  })

  const {
    formState: { errors: formErrors },
    control,
  } = form
  const pointOfCareFormLens = useLens({ control })
  const medicalPointOfCareField = useWatch({ control, name: MEDICAL_POINT_OF_CARE_FIELD })
  const actualPointOfCareField = useWatch({ control, name: ACTUAL_POINT_OF_CARE_FIELD })
  const actualPointOfCareItems = pointOfCareItems.filter(isSelectableAsActual(medicalPointOfCareField))
  const setReferralTimeToTreat = useReferralStoreContext((state) => state.setTimeToTreat)

  useEffect(() => {
    setReferralTimeToTreat(medicalPointOfCareField === "SPECIALIST" ? "LATER" : null)
  }, [medicalPointOfCareField, setReferralTimeToTreat])

  return (
    <FormProvider {...form}>
      <CardBodyHeader data-testid="point-of-care-title">
        <CardBodyHeaderTitle>{t("episodeOfCare.progressNote.pointOfCare.title")}</CardBodyHeaderTitle>
        <div className="self-center pr-4">
          <FormStatus formControl={form} />
        </div>
      </CardBodyHeader>
      <CardBodyContent data-testid="point-of-care-content">
        <ErrorBoundary
          fallback={
            <div className="flex justify-center gap-4 pt-0.5 align-middle">
              <p className="flex items-center">{t("common.somethingWentWrong")}</p>
              <Button variant="secondary" onClick={() => location.reload()}>
                {t("common.button.refresh")}
              </Button>
            </div>
          }>
          <div className="grid w-full gap-4 @lg/patient-layout:grid-cols-2">
            <ProgressNoteSelect
              label={t("episodeOfCare.progressNote.pointOfCare.medicalPointOfCare")}
              options={pointOfCareItems}
              testId="medical-point-of-care"
              defaultValue={medicalPointOfCareField}
              onSelect={onMedicalPointOfCareSelect}
              disabled={!!actualPointOfCareField}
              error={formErrors.medicalPointOfCare?.message ?? ""}
            />
            {medicalPointOfCareField !== "INCOMPLETE_TELECONSULTATION" &&
            medicalPointOfCareField !== "EXTERNAL_ASSISTANCE" ? (
              <ProgressNoteSelect
                label={t("episodeOfCare.progressNote.pointOfCare.actualPointOfCare")}
                options={actualPointOfCareItems}
                testId="actual-point-of-care"
                onSelect={onActualPointOfCareSelect}
                defaultValue={actualPointOfCareField ?? undefined}
                disabled={!medicalPointOfCareField}
                isClearable={true}
              />
            ) : null}
            <PointOfCareContent
              pointOfCareFormLens={pointOfCareFormLens}
              patientId={patientId}
              medgateServiceInfo={medgateServiceInfo}
              countryCode={country}
              timeToTreatByPointOfCare={timeToTreatByPointOfCare}
              episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
              diagnosisCode={diagnosisCode}
              medicalPointOfCare={medicalPointOfCareField}
              actualPointOfCare={actualPointOfCareField}
              hasCareProvider={hasCareProvider}
              setCareProvider={setCareProvider}
              onBetterDocCheck={onBetterDocCheck}
              onMedicalPointOfCareSelect={onMedicalPointOfCareSelect}
              onActualPointOfCareSelect={onActualPointOfCareSelect}
              onSpecialtySelect={onSpecialtySelect}
            />
            {episodeOfCareProgressNoteData.__typename === "de_ProgressNote" ? (
              <div className="col-span-2">
                <DeBetterDocResult betterDocResult={episodeOfCareProgressNoteData.betterDocResult} />
              </div>
            ) : null}
          </div>
        </ErrorBoundary>
      </CardBodyContent>
      <BetterDocRejectionReasonDialog
        open={isRejectionDialogOpen}
        isConfirming={isConfirmingBetterDocRejection}
        onCancel={handleRejectionCancel}
        onConfirm={handleRejectionConfirm}
        onCloseAutoFocus={handleRejectionDialogCloseAutoFocus}
      />
    </FormProvider>
  )
}
